import {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import TabComponent, {TabData} from '../../../../../app/common/components/TabComponent';
import {useGetLimitation, useGetTrunkNumeros} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import RestrictionAppelsSortants from './RestrictionAppelsSortants';
import RestrictionAppelsEntrants from './RestrictionAppelsEntrants';
import RestrictionAntiSpam from './RestrictionAntiSpam';
import RestrictionFiltrageIp from './RestrictionFiltrageIp';

/**
 * « Restriction d'appels » (Mes services > Téléphonie). Reproduit les 4 onglets
 * réels partagés côté legacy par la fonction LimitationOngletHaut : appels sortants
 * (Limitation.aspx), appels entrants (LimitationsOutgoing.aspx — nom trompeur, c'est bien un
 * blacklisting entrant), filtrage anti-spam (LimitationAntiSpam.aspx, maquette) et filtrage IP
 * (FilteredIP.aspx) — les trois premiers branchés sur api-telephonie. La page de sélection de
 * prestation (LimitationPrestation.aspx) n'a pas d'équivalent ici : l'app gère déjà la
 * sélection de prestation en amont de cette page.
 *
 * Les onglets sortants/entrants/IP suivent les capacités publiées par l'API (`capabilities`) —
 * absents si la capacité manque, celui de filtrage IP restant présent mais grisé si elle existe
 * mais est verrouillée côté plateforme. L'onglet anti-spam, lui, ne suit aucun
 * indicateur : la plateforme ne porte aucun flag pour lui, seulement une logique d'environnement
 * et de marque côté Kiosk — il reste affiché sans condition tant que ce point n'est pas tranché.
 */
/**
 * Masquage temporaire de l'onglet « Filtrage anti-spam » : repasser à `false`
 * pour le réafficher.
 */
const FILTRAGE_ANTISPAM_MASQUE = true;

interface RestrictionAppelsProps {
  serviceId: number;
  isActive?: boolean;
  /** Numéro à présélectionner dans les sous-onglets sortants/entrants
   * (venu de « Gérer vos numéros »). */
  numeroInitial?: {numero: string; numeroId: number} | null;
}

const RestrictionAppels = ({
  serviceId,
  isActive = true,
  numeroInitial,
}: RestrictionAppelsProps): JSX.Element | null => {
  const intl = useIntl();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [message, setMessage] = useAutoDismissMessage();
  const {data} = useGetTrunkNumeros(serviceId, isActive);
  const {data: limitation, isLoading: limitationLoading} = useGetLimitation(serviceId, isActive);
  const modificationEffectuee = intl.formatMessage({
    id: 'TELEPHONIE.RESTRICTION.MODIFICATION_EFFECTUEE',
  });

  const onConfirm = (): void => setMessage(modificationEffectuee);

  const tabs = useMemo<TabData[]>(() => {
    if (!limitation) {
      return [];
    }
    const {capabilities} = limitation;
    const liste: TabData[] = [];
    if (capabilities.outboundRestrictions) {
      liste.push({
        title: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TAB_SORTANTS'}),
        key: 'sortants',
        content: (
          <RestrictionAppelsSortants
            key={numeroInitial?.numeroId ?? 'tous'}
            serviceId={serviceId}
            onConfirm={onConfirm}
            numeroInitial={numeroInitial ? String(numeroInitial.numeroId) : undefined}
          />
        ),
      });
    }
    if (capabilities.blockedCallers) {
      liste.push({
        title: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TAB_ENTRANTS'}),
        key: 'entrants',
        content: (
          <RestrictionAppelsEntrants
            key={numeroInitial?.numero ?? 'tous'}
            serviceId={serviceId}
            onConfirm={onConfirm}
            numeroInitial={numeroInitial?.numero}
          />
        ),
      });
    }
    if (!FILTRAGE_ANTISPAM_MASQUE) {
      liste.push({
        title: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TAB_ANTISPAM'}),
        key: 'antispam',
        content: <RestrictionAntiSpam onConfirm={onConfirm} />,
      });
    }
    if (capabilities.ipFiltering) {
      liste.push({
        title: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TAB_IP'}),
        key: 'ip',
        content: <RestrictionFiltrageIp serviceId={serviceId} onConfirm={onConfirm} />,
        disabled: capabilities.ipFilteringLocked,
      });
    }
    return liste;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitation, serviceId, numeroInitial]);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((tab) => tab.key === activeKey)) {
      setActiveKey(tabs[0].key);
    }
  }, [tabs, activeKey]);

  if (limitationLoading || !limitation || activeKey === null) {
    return null;
  }

  return (
    <div className="mt-4">
      {data?.prestation && (
        <h2 className="text-primary fw-bold text-uppercase mb-5">
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TITLE'})} {data.prestation}
        </h2>
      )}
      {message && <div className="fw-bold text-success mb-3">{message}</div>}
      <TabComponent
        tabData={tabs}
        activeKey={activeKey}
        onTabChange={(key) => {
          setMessage(null);
          setActiveKey(key);
        }}
      />
    </div>
  );
};

export default RestrictionAppels;
